import {
  getSupabaseClient,
  getSupabaseConfig,
  getSupabaseStorageKeyFromDirectUrl,
  xhrFetch,
} from "@/lib/supabase/client";

export type SupabaseSessionUser = {
  id: string;
  email: string | null;
  metadata?: Record<string, unknown>;
};

function isAbortError(err: unknown) {
  const anyErr = err as any;
  const name = String(anyErr?.name ?? "");
  const msg = String(anyErr?.message ?? "");
  return name === "AbortError" || msg.toLowerCase().includes("signal is aborted");
}

function base64UrlDecode(input: string) {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = normalized.length % 4;
  const padded = pad ? normalized + "=".repeat(4 - pad) : normalized;

  if (typeof globalThis.atob === "function") {
    return globalThis.atob(padded);
  }

  // Fallback for environments without atob
  return Buffer.from(padded, "base64").toString("binary");
}

function readJwtClaims(token: string): Record<string, any> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json) as any;
  } catch {
    return null;
  }
}

function readSessionUserFromStorage(): SupabaseSessionUser | null {
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl));
    if (!storageKey) return null;

    const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
    if (!raw) return null;

    const parsed = JSON.parse(raw) as any;
    const u = parsed?.user;

    let id = typeof u?.id === "string" ? u.id : String(u?.id ?? "");
    let email = typeof u?.email === "string" ? u.email : null;
    let metadata = (u?.user_metadata ?? {}) as Record<string, unknown>;

    // Some Supabase token responses omit the `user` object.
    // Derive minimal identity from the JWT so the app can proceed without any extra network calls.
    if (!id) {
      const accessToken = typeof parsed?.access_token === "string" ? parsed.access_token : "";
      const claims = accessToken ? readJwtClaims(accessToken) : null;
      const sub = typeof claims?.sub === "string" ? claims.sub : "";
      id = sub;
      if (!email) {
        const em = typeof claims?.email === "string" ? claims.email : "";
        email = em || null;
      }
      if (!metadata || !Object.keys(metadata).length) {
        metadata = (claims?.user_metadata ?? claims?.app_metadata ?? {}) as Record<string, unknown>;
      }
    }

    if (!id) return null;

    return {
      id,
      email,
      metadata,
    };
  } catch {
    return null;
  }
}

export async function getSessionUser(): Promise<SupabaseSessionUser | null> {
  // Fast path: read directly from storage.
  // This bypasses supabase-js session recovery logic which can hang in some embedded environments.
  const fromStorage = readSessionUserFromStorage();
  if (fromStorage) return fromStorage;

  const supabase = getSupabaseClient();

  // Fallback: supabase-js API.
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const fromSession = sessionData.session?.user;

    if (fromSession) {
      return {
        id: fromSession.id,
        email: fromSession.email ?? null,
        metadata: (fromSession.user_metadata ?? {}) as Record<string, unknown>,
      };
    }

    return null;
  } catch (err) {
    // Supabase can abort in-flight refresh/session checks; treat as "no session" instead of throwing
    // (prevents noisy unhandledrejection logs in the browser).
    if (isAbortError(err)) return null;
    throw err;
  }
}

export async function signInWithPassword(email: string, password: string) {
  const supabase = getSupabaseClient();

  // Normal path: browser -> Supabase.
  // Some environments block external requests; when that happens, the UI will time out.
  // We keep this default behavior so hosted setups work as expected.
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signInWithPasswordViaServer(email: string, password: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const res = await xhrFetch("/api/auth/signin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      signal: controller.signal,
    });

    if (res.status === 499) {
      throw new Error("/api/auth/signin zaman aşımı");
    }

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      const msg =
        typeof json?.error === "string"
          ? json.error
          : typeof json?.message === "string"
            ? json.message
            : "Giriş yapılamadı";
      const err = new Error(msg);
      (err as any).status = res.status;
      (err as any).needsVerification = Boolean(json?.needsVerification);
      throw err;
    }

    const session = json?.session;
    const access_token = typeof session?.access_token === "string" ? session.access_token : "";
    const refresh_token = typeof session?.refresh_token === "string" ? session.refresh_token : "";
    const expires_in = typeof session?.expires_in === "number" ? session.expires_in : Number(session?.expires_in ?? 0) || null;
    const token_type = typeof session?.token_type === "string" ? session.token_type : null;
    const user = session?.user ?? null;

    if (!access_token || !refresh_token) {
      throw new Error("Sunucudan oturum alınamadı");
    }

    return {
      access_token,
      refresh_token,
      expires_in,
      token_type,
      user,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if ((err instanceof DOMException && err.name === "AbortError") || msg.toLowerCase().includes("signal is aborted")) {
      throw new Error("/api/auth/signin zaman aşımı");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function devSignInViaServer(email: string, password: string) {
  const res = await xhrFetch("/api/auth/dev-signin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Giriş yapılamadı");
  }

  const user = json?.user;
  const data = {
    id: String(user?.id ?? ""),
    email: typeof user?.email === "string" ? user.email : null,
    metadata: (user?.user_metadata ?? {}) as Record<string, unknown>,
  } satisfies SupabaseSessionUser;

  try {
    globalThis.localStorage?.setItem("dev_auth_user", JSON.stringify(data));
  } catch {
    // ignore
  }

  globalThis.dispatchEvent?.(new Event("dev-auth-changed"));
  return data;
}

export function devSignOutLocal() {
  try {
    globalThis.localStorage?.removeItem("dev_auth_user");
  } catch {
    // ignore
  }
  globalThis.dispatchEvent?.(new Event("dev-auth-changed"));
}

export async function signUpWithPasswordViaServer(
  email: string,
  password: string,
  args?: {
    data?: Record<string, unknown>;
  },
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await xhrFetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, data: args?.data ?? {} }),
      signal: controller.signal,
    });

    if (res.status === 499) {
      throw new Error("/api/auth/signup zaman aşımı");
    }

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      const msg =
        typeof json?.error === "string"
          ? json.error
          : typeof json?.message === "string"
            ? json.message
            : "Kayıt yapılamadı";
      const err = new Error(msg);
      (err as any).status = res.status;
      throw err;
    }

    // New flow: server does not create a session on signup; it sends a verification code.
    if (json?.needsVerification) {
      return { needsVerification: true as const };
    }

    // Backwards-compatible: if a session is returned, keep supporting it.
    const session = json?.session;
    const access_token = typeof session?.access_token === "string" ? session.access_token : "";
    const refresh_token = typeof session?.refresh_token === "string" ? session.refresh_token : "";
    const expires_in = typeof session?.expires_in === "number" ? session.expires_in : Number(session?.expires_in ?? 0) || null;
    const token_type = typeof session?.token_type === "string" ? session.token_type : null;
    const user = session?.user ?? null;

    if (!access_token || !refresh_token) {
      throw new Error("Sunucudan yanıt alınamadı");
    }

    return { access_token, refresh_token, expires_in, token_type, user };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if ((err instanceof DOMException && err.name === "AbortError") || msg.toLowerCase().includes("signal is aborted")) {
      throw new Error("/api/auth/signup zaman aşımı");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function signUpWithPassword(
  email: string,
  password: string,
  args?: {
    data?: Record<string, unknown>;
    emailRedirectTo?: string;
  },
) {
  const supabase = getSupabaseClient();
  const data = args?.data;
  const emailRedirectTo = args?.emailRedirectTo;

  return supabase.auth.signUp({
    email,
    password,
    options: {
      ...(data ? { data } : null),
      ...(emailRedirectTo ? { emailRedirectTo } : null),
    },
  });
}

export async function resendSignupConfirmationViaServer(email: string, emailRedirectTo?: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const res = await xhrFetch("/api/auth/resend-signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...(emailRedirectTo ? { emailRedirectTo } : null) }),
      signal: controller.signal,
    });

    if (res.status === 499) {
      throw new Error("/api/auth/resend-signup zaman aşımı");
    }

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      const msg = typeof json?.error === "string" ? json.error : "Mail gönderilemedi";
      throw new Error(msg);
    }

    return { data: { ok: true }, error: null as any };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if ((err instanceof DOMException && err.name === "AbortError") || msg.toLowerCase().includes("signal is aborted")) {
      throw new Error("Doğrulama maili zaman aşımına uğradı");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function resendSignupConfirmation(email: string, emailRedirectTo?: string) {
  const supabase = getSupabaseClient();
  return supabase.auth.resend({
    type: "signup",
    email,
    options: emailRedirectTo ? { emailRedirectTo } : undefined,
  });
}

export async function refreshSessionViaServer(refresh_token: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const res = await xhrFetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token }),
      signal: controller.signal,
    });

    if (res.status === 499) {
      throw new Error("/api/auth/refresh zaman aşımı");
    }

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      const msg = typeof json?.error === "string" ? json.error : "Oturum yenilenemedi";
      const err = new Error(msg);
      (err as any).status = res.status;
      (err as any).needsVerification = Boolean(json?.needsVerification);
      throw err;
    }

    const session = json?.session;
    const access_token = typeof session?.access_token === "string" ? session.access_token : "";
    const new_refresh_token = typeof session?.refresh_token === "string" ? session.refresh_token : "";
    const expires_in = typeof session?.expires_in === "number" ? session.expires_in : Number(session?.expires_in ?? 0) || null;
    const token_type = typeof session?.token_type === "string" ? session.token_type : null;
    const user = session?.user ?? null;

    if (!access_token || !new_refresh_token) {
      throw new Error("Sunucudan oturum alınamadı");
    }

    return {
      access_token,
      refresh_token: new_refresh_token,
      expires_in,
      token_type,
      user,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if ((err instanceof DOMException && err.name === "AbortError") || msg.toLowerCase().includes("signal is aborted")) {
      throw new Error("/api/auth/refresh zaman aşımı");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function requestPasswordResetViaServer(email: string) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  try {
    const res = await xhrFetch("/api/auth/request-password-reset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      signal: controller.signal,
    });

    if (res.status === 499) {
      throw new Error("/api/auth/request-password-reset zaman aşımı");
    }

    const json = (await res.json().catch(() => null)) as any;
    if (!res.ok) {
      const msg = typeof json?.error === "string" ? json.error : "Şifre sıfırlama maili gönderilemedi";
      throw new Error(msg);
    }

    return { ok: true } as const;
  } catch (err) {
    const msg = err instanceof Error ? err.message : "";
    if ((err instanceof DOMException && err.name === "AbortError") || msg.toLowerCase().includes("signal is aborted")) {
      throw new Error("Şifre sıfırlama isteği zaman aşımına uğradı");
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function verifyEmailCodeViaServer(email: string, code: string) {
  const res = await xhrFetch("/api/auth/verify-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Doğrulama başarısız");
  }

  return { ok: true } as const;
}

export async function bypassConfirmEmail(args: { email?: string; userId?: string }) {
  const res = await xhrFetch("/api/auth/bypass-confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İşlem başarısız");
  }

  return json as { ok: true; userId: string; email: string | null };
}

export async function signOut() {
  // Prefer local sign-out via storage operations.
  // supabase-js signOut can attempt network calls in some embedded environments.
  try {
    const { directUrl } = getSupabaseConfig();
    const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl));
    if (storageKey) {
      globalThis.localStorage?.removeItem(storageKey);
    }
  } catch {
    // ignore
  }

  try {
    const supabase = getSupabaseClient();
    const p = (supabase.auth as any)?.signOut?.({ scope: "local" });
    if (p && typeof (p as any).then === "function") {
      (p as Promise<any>).catch(() => null);
    }
  } catch {
    // ignore
  }

  globalThis.dispatchEvent?.(new Event("supabase-session-changed"));
  return { error: null as any };
}
